import React, { useState } from "react";
import { Alert, Button, Form, Select, Space, Statistic, Tooltip, message } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { ResponsiveCard } from "../components/ResponsiveCard";
import { WizardService } from "../../services/WizardService";
import { SpmCrawlResult } from "../../types/spmIntegrationInfo";
import { MEDIATION_ENGINES } from "../mapping/mediationEngines";
import { describeCrawlerError } from "./crawlerErrors";

const SKIPPED_TOOLTIP =
  "This total lumps several outcomes together: a decoder failure, an ordinary 'no matching " +
  "adapter/SDK row' miss, and a source whose tag fetch threw all increment `skipped` " +
  "identically. A non-zero value is not by itself a failure — for the per-tag breakdown " +
  "(matched / no_target_row / unparsable / superseded, and newer values as they're added), " +
  "see GET /v4/spm/outcomes.";

/**
 * Manual trigger for the per-provider SPM crawl (`POST v4/crawler/spm/{provider}`).
 *
 * Deliberately separate from `MediationCrawlerCard`: that one crawls Maven/CocoaPods versions
 * per platform, this one walks git tags of Swift packages and is platform-less (iOS only, by
 * definition). Folding it into the mediation cards would imply an Android SPM crawl exists.
 */
export const SpmCrawlerCard = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [provider, setProvider] = useState<string | undefined>();
  const [result, setResult] = useState<SpmCrawlResult | undefined>();

  const onSubmit = () => {
    if (!provider) return;
    setIsLoading(true);
    setResult(undefined);
    WizardService.Crawler.spmCrawl(provider)
      .then((response) => setResult(response))
      .catch((error) => message.error(describeCrawlerError(error)))
      .finally(() => setIsLoading(false));
  };

  return (
    <ResponsiveCard contentPadding="1rem" title="SPM (Swift Packages)">
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: "1rem" }}
        message="Not recorded in System Health"
        description={
          <>
            This trigger writes materialized SPM rows but creates no crawler run
            record — it will not appear in{" "}
            <Link to="/wizard/system-health">System Health</Link>. Only the full
            scheduled reindex does that.
          </>
        }
      />
      <Form layout="vertical" onFinish={onSubmit}>
        <Form.Item label="Provider" required>
          <Select
            placeholder="Select provider"
            value={provider}
            onChange={setProvider}
            options={MEDIATION_ENGINES.map((e) => ({
              value: e.engine,
              label: e.label,
            }))}
          />
        </Form.Item>
        <Form.Item>
          <Button
            loading={isLoading}
            disabled={!provider}
            type="primary"
            htmlType="submit"
          >
            Crawl SPM
          </Button>
        </Form.Item>
      </Form>

      {result && (
        <Space size="large" wrap>
          <Statistic
            title="Upserted"
            value={result.upserted}
            valueStyle={{ color: "#3f8600" }}
          />
          <Statistic
            title={
              <Tooltip title={SKIPPED_TOOLTIP}>
                <Space size={4}>
                  Skipped
                  <QuestionCircleOutlined />
                </Space>
              </Tooltip>
            }
            value={result.skipped}
          />
          <Tooltip title="Several source rows resolved to the same (target, product) and were merged into one upsert — expected when a package serves multiple mappings.">
            <Statistic title="Collapsed" value={result.collapsed} />
          </Tooltip>
        </Space>
      )}
    </ResponsiveCard>
  );
};
